import { LoadingButton } from '@mui/lab';
import { Workbook } from 'exceljs';
import React, { useCallback, useState } from 'react';
import { RubricCore } from 'Types/dealGPT';

import { AnalyticsEvent, useAnalytics } from '../../../../components/Providers/AnalyticsProvider';
import { downloadExcel } from '../../../../util/excel';
import { getTileColor } from './rubricUtils';

/**
 * Generates an Excel workbook containing rubric core data.
 *
 * @param {string} rubricType - The type of rubric, either 'LMH' or 'Numeric'.
 * @param {Record<string, Record<string, RubricCore[]>>} rubricCoreByArea - An object containing rubric core data organized by area and dimension.
 * @returns {Promise<Workbook>} - A promise that resolves to the generated Excel workbook.
 *
 * The generated workbook contains:
 * - A title row with merged cells for the maturity level.
 * - A header row with area, dimension, and maturity level columns.
 * - Data rows with rubric core descriptions for each area and dimension.
 */
const generateExcel = async (
  rubricType: string,
  rubricCoreByArea: Record<string, Record<string, RubricCore[]>>
): Promise<Workbook> => {
  const isLMH = rubricType === 'LMH';
  const workbook = new Workbook();
  const worksheet = workbook.addWorksheet('Rubric Core');

  // Add the first row and  Merge C1 to G1 to create a title row
  worksheet.addRow(['', '', 'Maturity Level']);
  worksheet.mergeCells(
    isLMH
      ? 'C1:E1'
      : (() => {
          const maxRating = Number(rubricType);
          return `C1:${String.fromCharCode('C'.charCodeAt(0) + maxRating - 1)}1`;
        })()
  );

  const scaleValues = isLMH
    ? ['Low', 'Medium', 'High']
    : (() => {
        const values = [];
        for (let i = 1; i <= Number(rubricType); i++) {
          values.push(i);
        }
        return values;
      })();

  // Add the second row, the actual header
  worksheet.addRow(['Area', 'Dimension', ...scaleValues]);

  // Add the rubric core data
  Object.entries(rubricCoreByArea).forEach(([area, areaRubrics]) => {
    Object.entries(areaRubrics).forEach(([dimension, dimensionRubrics]) => {
      const row = worksheet.addRow([
        area,
        dimension,
        ...scaleValues.map(
          (displayName) =>
            dimensionRubrics.find(
              (core) =>
                core.maturityLevelDisplayName === displayName || core.maturityLevel === displayName
            )?.description
        ),
      ]);

      row.alignment = { vertical: 'middle' };
    });
  });

  // Apply styles to the title row
  const titleRow = worksheet.getRow(1);
  titleRow.height = 30;
  titleRow.eachCell((cell, colNumber) => {
    if (colNumber <= 2) return; // Skip the first two cells

    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: '4472C4' },
    };
    cell.alignment = { horizontal: 'center' };
    cell.font = { color: { argb: 'FFFFFF' }, bold: true, size: 18 };
  });

  // Apply styles and maturity level colors to the header row
  const headerRow = worksheet.getRow(2);
  headerRow.height = 30;
  headerRow.eachCell((cell, colNumber) => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: {
        argb:
          colNumber > 2
            ? getTileColor(
                0,
                colNumber - 2,
                parseInt(rubricType === 'LMH' ? '3' : rubricType)
              ).replace('#', '')
            : '4472C4',
      },
    };
    cell.alignment = { horizontal: 'center' };
    cell.font = { color: { argb: 'FFFFFF' }, bold: true, size: 18 };
  });

  // Set minimum width for all columns
  worksheet.columns.forEach((column) => {
    column.width = 50;
  });

  // Apply styles to the data rows and calculate the required height
  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber <= 2) return;

    let maxHeight = 0;
    row.eachCell((cell, colNumber) => {
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
      cell.alignment = { wrapText: true };
      cell.font = { size: 14, color: { argb: colNumber === 1 ? 'FFFFFF' : '000000' } };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: colNumber === 1 ? '002060' : 'FFFFFF' },
      };

      if (typeof cell.value === 'string') {
        const lines = Math.ceil(cell.value.length / 50); // Estimate number of lines (assuming column width is 50 characters)
        const cellHeight = 15 + lines * 15; // Base height + extra lines
        maxHeight = Math.max(maxHeight, cellHeight); // Track the largest required height
      }
    });

    // Set the row height based on the largest content
    row.height = maxHeight || 50;
  });

  return workbook;
};

interface DownloadRubricCoreButtonProps {
  projectId: string;
  rubricType: string;
  scope: string | undefined;
  rubricCore: Record<string, Record<string, RubricCore[]>>;
}

export const DownloadRubricCoreButton: React.FC<DownloadRubricCoreButtonProps> = ({
  projectId,
  rubricType,
  scope,
  rubricCore,
}) => {
  const [loading, setLoading] = useState(false);
  const analytics = useAnalytics();

  const onClick = useCallback(async () => {
    setLoading(true);
    analytics.event(
      AnalyticsEvent.WM_IA_A_RUBRIC_DOWNLOAD,
      { value: `Scope=${scope}` },
      {
        projectId,
        scope,
      }
    );
    const excelContent = await generateExcel(rubricType, rubricCore);
    downloadExcel(excelContent, `${scope} Rubric.xlsx`);
    setLoading(false);
  }, [scope, rubricType, rubricCore, analytics, projectId]);

  if (Object.keys(rubricCore).length === 0) return null;

  return (
    <LoadingButton loading={loading} onClick={onClick}>{`Download ${scope} Rubric`}</LoadingButton>
  );
};
